import find from 'lodash/find';
import { push } from 'connected-react-router';

// API
import api from 'app/shared/utils/api';

// Actions
import { setActiveId } from './UIActions';
import { createMarketingLead, createMarketUpdate } from 'app/shared/flux/actions/MarketingActions';

// Models
import AutocompleteAddress from 'app/shared/models/AutocompleteAddress';
import Address from 'app/shared/models/Address';
import Comparables from 'app/shared/models/Comparables';

// Queries
import { getCompsOnlyQuery, getFullQuery } from 'app/shared/utils/queries';

// Misc / utils
import { trackEvent, trackGtmEvent } from 'app/shared/utils/analytics/trackEvent';
import { gaEvents } from 'app/shared/constants/AnalyticsConstants';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { encodeFullAddressForUrl } from 'app/shared/utils/urlUtils';
import type { QueryDataObjectType } from 'app/shared/types/QueryDataObjectType';
import { augmentError } from 'app/shared/utils/errors';
import constants from 'app/shared/flux/constants';
import errorConstants from 'app/shared/constants/ErrorConstants';

// Types
import type {
    AutocompleteAddressApiType,
    ByAddressApiType,
    ComparablesApiType,
    getListingForAddressApiType,
    ListingApiErrorType
} from 'app/shared/types/ApiResponseTypes';

type AugmentedAutocompleteAddressType = AutocompleteAddress & {
    isRecent: boolean;
    unit: string;
};

// ActionCreators... <verb><Noun>
export const goToAddressItem = (addressDataObject: AugmentedAutocompleteAddressType) => {
    // @ts-ignore
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const userEmail = state.user.email;

            dispatch({
                type: constants.ADD_RECENT_SEARCH,
                payload: addressDataObject
            });

            // Call marketing lead API
            dispatch(
                createMarketingLead({
                    fullAddress: addressDataObject.name || '',
                    unit: addressDataObject.unit,
                    email: userEmail
                })
            );

            // Fetch address and comparables
            const { userAddress } = await dispatch(getAddressAndComparables(addressDataObject));

            // Call marketing update API
            dispatch(
                createMarketUpdate({
                    email: userEmail,
                    zpid: userAddress.zpid,
                    price: userAddress.rentZestimate,
                    lat: userAddress.lat,
                    lon: userAddress.lon
                })
            );

            const encodedFullAddress = encodeFullAddressForUrl({
                address: addressDataObject.name,
                unit: addressDataObject.unit
            });

            trackEvent(gaEvents.WITH_COMPS_WITH_ZESTIMATE);
            trackGtmEvent(gaEvents.GTM_CUSTOM_RENT_ZESTIMATE);

            return dispatch(push(`${pathsManifest.PriceMyRentalPageResults}/${encodedFullAddress}`));
        } catch (err) {
            // @ts-ignore
            const shouldLogAction = !err.data || !err.data.action;
            augmentError(err, {
                ...(shouldLogAction && {
                    action: 'Error in AddressActions#goToAddressItem'
                })
            });

            throw err;
        }
    };
};

export const getComparables = (params: QueryDataObjectType) => {
    // @ts-ignore
    return async (dispatch) => {
        // Call partial query from API that gets comparables
        const requestBody = getCompsOnlyQuery(params);
        const { data } = await dispatch(fetchGraphQlData(requestBody));
        const userComps = dispatch(setComparables(data));

        return { userComps };
    };
};

export const getAddressAndComparables = (params: AugmentedAutocompleteAddressType) => {
    // @ts-ignore
    return async (dispatch) => {
        // Call full query from API
        const requestBody = getFullQuery(params);
        const { data, errors } = await dispatch(fetchGraphQlData(requestBody));
        const userAddress = await dispatch(setAddress(data));
        const hasZestimate = !find(errors, (error) => error.code === errorConstants.RENT_ZESTIMATE_NOT_FOUND);
        const hasCompsPrices = !find(errors, (error) => error.code === errorConstants.GET_COMPS_PRICE_EMPTY);

        if (!hasCompsPrices && !hasZestimate) {
            throwNonCriticalError({
                message: "We don't have enough data for this address",
                code: errorConstants.RENT_ZESTIMATE_NOT_FOUND
            });
        }

        if (hasCompsPrices) {
            await dispatch(setComparables(data));
        }

        return {
            userAddress
        };
    };
};

// API fetches
export const fetchAutocompleteData = (partialAddress: string) => {
    // @ts-ignore
    return async (dispatch) => {
        try {
            const data = await dispatch(api.address.autocomplete(partialAddress));
            const addressSuggestions = data.addresses.map((address: AutocompleteAddressApiType) => {
                return new AutocompleteAddress(address);
            });

            return addressSuggestions;
        } catch (error) {
            augmentError(error, {
                action: 'Error in AddressActions#fetchAutocompleteData'
            });

            throw error;
        }
    };
};

export const fetchGraphQlData = (requestBody: string) => {
    // @ts-ignore
    return async (dispatch) => {
        try {
            const { data, errors } = await dispatch(api.address.graphQlListing(requestBody));

            /*
                Throw on major errors, otherwise handle errors in places where they cause issues
           */
            if (errors.length > 0) {
                if (containsError(errors, errorConstants.PROPERTY_DETAILS_NOT_FOUND)) {
                    // If listing can't be found by our API, throw error and STOP
                    trackEvent(gaEvents.CANNOT_FIND_ADDRESS);
                    throwNonCriticalError({
                        message: 'We were unable to match this address',
                        code: errorConstants.PROPERTY_DETAILS_NOT_FOUND
                    });
                }

                if (containsError(errors, errorConstants.GET_COMPS_PRICE_EMPTY)) {
                    trackEvent(gaEvents.CANNOT_FIND_COMPS);
                }

                if (containsError(errors, errorConstants.RENT_ZESTIMATE_NOT_FOUND)) {
                    trackEvent(gaEvents.CANNOT_FIND_ADDRESS_ZESTIMATE);
                }
            }

            return { data, errors };
        } catch (error) {
            augmentError(error, {
                action: 'Error in AddressActions#fetchGraphQlData'
            });

            throw error;
        }
    };
};

// Setters
export const setAddress = (data: ByAddressApiType) => {
    // @ts-ignore
    return async (dispatch, getState) => {
        const addressData = {
            ...data.byAddress.floorplans[0],
            geo: data.byAddress.geo
        };

        // Check if the user logged in - if so, check if they already have a listing for this address
        const isUserLoggedIn: boolean = getState().user.loggedIn;
        if (isUserLoggedIn) {
            const { street, city, state, zip, unit } = addressData.address;
            const fullAddress = street + ',' + city + ', ' + state + ' ' + zip;

            await dispatch(api.address.getListingForAddress(fullAddress, unit))
                .then((res: getListingForAddressApiType) => {
                    addressData.addressHasListing = res.listingFound;
                    // If listingFound false, then there is no listing (a.k.a undefined)
                    if (res.listingFound) {
                        addressData.zrmListingId = res.listing.listingId;
                    }
                })
                .catch((error: { message: string }) => {
                    augmentError(error, {
                        action: 'Error in AddressActions#setAddress'
                    });

                    throw error;
                });
        }

        // Set the user's address
        const modeledAddressData = new Address(addressData);

        dispatch({
            type: constants.SET_ADDRESS,
            payload: modeledAddressData
        });

        modeledAddressData.listingId && dispatch(setActiveId(modeledAddressData.listingId));

        return modeledAddressData;
    };
};

export const setComparables = (data: ByAddressApiType) => {
    // @ts-ignore
    return (dispatch, getState) => {
        const {
            floorplans,
            listingRanking,
            lowestPriceIncluded: min,
            highestPriceIncluded: max
        } = data.byAddress.similarFloorplans;

        if (floorplans.length === 0) {
            trackEvent(gaEvents.CANNOT_FIND_COMPS);
        }

        // Set the user's comparables
        const compsItems = floorplans.map((comp: ComparablesApiType) => {
            return new Comparables(comp);
        });

        const userProperty = getState().address;
        compsItems.splice(listingRanking, 0, userProperty);
        const userPropertyIdx = compsItems.findIndex((comp) => comp.isUsersComp);

        dispatch({
            type: constants.SET_COMPARABLES,
            payload: {
                min,
                max,
                items: compsItems,
                userPropertyIdx
            }
        });

        // set activeId to userProperty ID after comps load to scroll to home
        dispatch(setActiveId(userProperty.listingId));

        return compsItems;
    };
};

// Errors
const throwNonCriticalError = ({ message, code }: { message: string; code: string }) => {
    throw {
        nonCriticalError: true,
        errorCode: code,
        errorMsg: message
    };
};

const containsError = (errors: Array<ListingApiErrorType>, errorConstant: string) => {
    return find(errors, (error) => error.code === errorConstant);
};
